import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { useMenuEvents } from '@/shared/hooks'
import { GlobalShortcuts } from '@/shared/components/GlobalShortcuts'
import { useTransitionStore, useUpdateStore } from '@/shared/stores'
import { useSettingsStore } from '@/shared/stores/settings-store'
import { useUIStore } from '@/shared/stores/ui-store'
import { AgentSessionModal } from '@/features/queue/components/AgentSessionModal'
import { CustomerHistoryModal } from '@/features/queue/components/CustomerHistoryModal'
import { ReviewThreadModal } from '@/features/queue/components/ReviewThreadModal'
import { DiscussExecutionModal } from '@/features/queue/components/DiscussExecutionModal'
import { useQueueStore } from '@/features/queue/store'
import { useAuthChecked, useOperator } from '@/features/auth'
import { IS_TAURI } from '@/lib/platform'

/**
 * Local-Claude right rail — desktop only (Task 4093).
 *
 * `claude-sessions` is the single largest Tauri consumer in the app: 19 `invoke`
 * commands driving the Claude CLI sidecar, plus `plugin-fs`, `plugin-shell` and
 * `plugin-dialog`. None of it has a browser counterpart — the sidecar is a local
 * process.
 *
 * Loaded through `lazy()` behind the build-time flag rather than imported at the
 * top of this file so the whole feature, and every Tauri module it reaches, is
 * absent from the web bundle. A static import guarded only at the render site
 * would still be bundled: the guard hides the panel, it does not remove the
 * dependency.
 */
const ClaudeSessionPanel = IS_TAURI
  ? lazy(() =>
      import('@/features/claude-sessions').then((m) => ({
        default: m.ClaudeSessionPanel,
      })),
    )
  : null

/**
 * AppShell visibility logic.
 *
 * Two modes, based on the operator's `landingPage` setting:
 *
 *   1. Kodeon-splash mode (default):
 *      The shell is meant to be REAL UI immediately. The sidebar is fully
 *      interactive from the moment the page is reached, and the
 *      `WelcomePage` occupies the empty content area. The brand "KODEON"
 *      is rendered by the particle canvas behind the (transparent)
 *      WelcomePage. We do NOT hide the shell — opacity is always 1.
 *
 *   2. Specific-page mode:
 *      The destination page (events, customers, etc.) is being revealed
 *      via the slick "particles trace UI then fade away" sequence.
 *      The shell is hidden during the trace + hold phases and fades in
 *      during 'shell-ui-revealing'.
 */
function shellVisibility(
  phase: ReturnType<typeof useTransitionStore.getState>['phase'],
  isKodeonMode: boolean,
): { opacity: number; immediate: boolean } {
  // In Kodeon-splash mode the shell is always fully visible. The particles
  // never trace it, so there's nothing for it to "emerge from".
  if (isKodeonMode) {
    return { opacity: 1, immediate: true }
  }

  // Specific-page mode: hide while particles trace + hold, fade in during
  // 'shell-ui-revealing', then stay visible.
  if (phase === 'morphing-to-shell' || phase === 'shell-trace-hold') {
    return { opacity: 0, immediate: true }
  }
  if (phase === 'shell-ui-revealing') {
    return { opacity: 1, immediate: false }
  }
  return { opacity: 1, immediate: true }
}

export function AppShell() {
  // Menu item click events from Tauri are handled by useMenuEvents
  useMenuEvents()

  const phase = useTransitionStore((s) => s.phase)
  const reduceMotion = useSettingsStore((s) => s.reduceMotion)
  const landingPage = useSettingsStore((s) => s.landingPage)
  const isKodeonMode = landingPage.kind === 'kodeon-splash'
  // The legacy right-hand "Stream agents" SessionPanel was retired in favour of
  // the top-level /playground page. Only the Local Claude panel still lives in
  // the right rail.
  const { claudePanelOpen } = useUIStore()

  // ── Boot navigation ─────────────────────────────────────────────────────
  // The browser history persists across reloads (Tauri / vite dev), so a
  // reload while the user was on /events would have RouterProvider re-mount
  // on /events and the user would never see the configured landing page.
  // Override that on every fresh AppShell mount: navigate to the configured
  // landing page once. useLayoutEffect runs synchronously after the initial
  // commit but before paint, so the user doesn't visibly see /events
  // before we replace it with /welcome (or whatever they configured).
  const navigate = useNavigate()
  const location = useLocation()
  const hasBootNavigatedRef = useRef(false)
  useLayoutEffect(() => {
    if (hasBootNavigatedRef.current) return
    hasBootNavigatedRef.current = true
    const targetPath =
      landingPage.kind === 'kodeon-splash' ? '/welcome' : landingPage.route
    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: true })
    }
    // We deliberately want this to fire once per AppShell mount with the
    // landingPage / location values current at that mount. No reactive deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate])

  // ── Pre-emptive attention-queue load ────────────────────────────────────
  // The sidebar shows a badge with the number of outstanding attention
  // items (tool approvals + PR reviews) on the Queue nav entry. We want
  // that badge populated as soon as the operator lands in the shell —
  // before they ever click "Queue" — so they can see at a glance whether
  // there is anything waiting for them.
  //
  // startPolling() does an initial fetchAttentionItems()/fetchAssignments()
  // and then sets up the 10-minute background interval. It's a no-op if
  // polling is already running, so it's safe to call again on every
  // re-render. We gate on `authChecked` + an operator being selected so
  // the request is made with a valid auth context.
  const startQueuePolling = useQueueStore((s) => s.startPolling)
  const stopQueuePolling = useQueueStore((s) => s.stopPolling)
  const authChecked = useAuthChecked()
  const operator = useOperator()
  const operatorId = operator?.id
  useEffect(() => {
    if (!authChecked || !operatorId) return
    startQueuePolling(operatorId)
    // Polling persists for the lifetime of the authenticated shell —
    // we stop it only when the shell unmounts (sign-out / operator swap).
    return () => stopQueuePolling()
  }, [authChecked, operatorId, startQueuePolling, stopQueuePolling])

  // ── Background update check ──────────────────────────────────────────────
  // Drive the hourly self-update poll. startPolling() runs an immediate
  // check and then re-checks every hour; when it finds a newer build in the
  // Drive release folder the <UpdateIndicator> pill appears in the Header.
  // The check uses the operator's Google OAuth token, so gate on auth.
  const startUpdatePolling = useUpdateStore((s) => s.startPolling)
  const stopUpdatePolling = useUpdateStore((s) => s.stopPolling)
  useEffect(() => {
    if (!authChecked) return
    startUpdatePolling()
    return () => stopUpdatePolling()
  }, [authChecked, startUpdatePolling, stopUpdatePolling])

  // First-paint flicker guard. Only relevant for specific-page mode where
  // we need to hold opacity 0 until the canvas has had a frame to sample
  // our DOM for trace targets. In Kodeon mode we don't need this — the
  // shell is always visible from frame one.
  const [hasMounted, setHasMounted] = useState(isKodeonMode)
  useEffect(() => {
    if (isKodeonMode) {
      setHasMounted(true)
      return
    }
    const id = window.requestAnimationFrame(() => setHasMounted(true))
    return () => window.cancelAnimationFrame(id)
  }, [isKodeonMode])

  // ── Route-driven particle visibility ─────────────────────────────────────
  //
  // The particle canvas (StreamParticleCanvas) is mounted at the App root
  // and persists across navigations. Inside the AppShell the only page
  // where particles should ever be visible is /welcome (where they spell
  // KODEON). Everywhere else — events, customers, health, etc. — the
  // canvas should be hidden so the user doesn't see drifting dots behind
  // their dashboards.
  //
  // WelcomePage already toggles `particlesVisible` on its own mount/unmount,
  // but that's not enough on its own:
  //
  //   - If the AppShell renders directly into a non-welcome route (e.g.
  //     route-landing-page mode lands on /events, or boot navigation skips
  //     /welcome on a refresh), WelcomePage never mounts and its cleanup
  //     never fires, so the canvas stays visible.
  //
  //   - If the operator is switched while WelcomePage isn't mounted, the
  //     OperatorSelector flips `particlesVisible` back to true (it needs
  //     particles for its animated background) and nothing within the
  //     subsequent AppShell turns it back off until WelcomePage happens
  //     to be visited and unmounted.
  //
  // This effect is the canonical gate: on every route change inside the
  // AppShell, set `particlesVisible` from the path. It's deliberately
  // skipped during shell-* transitional phases so the "particles trace
  // the shell" entrance animation isn't clobbered to opacity 0 mid-trace.
  // Once a transition lands ('shell-revealed' or 'particles'), this effect
  // re-runs (via the phase dep) and reasserts the route-based truth.
  useEffect(() => {
    const isTransitioning =
      phase === 'morphing' ||
      phase === 'unmorphing' ||
      phase === 'morphing-to-shell' ||
      phase === 'shell-trace-hold' ||
      phase === 'shell-ui-revealing' ||
      phase === 'shell-particles-fading'
    if (isTransitioning) return
    const shouldShow = location.pathname === '/welcome'
    useTransitionStore.getState().setParticlesVisible(shouldShow)
  }, [location.pathname, phase])

  const vis = shellVisibility(phase, isKodeonMode)
  const opacity = !hasMounted && !reduceMotion ? 0 : vis.opacity
  // 500ms ease-out, matched to the SHELL_UI_FADE_MS constant. Using a
  // long-tail cubic-bezier so the fade-in feels "settled" rather than rushed.
  const transition =
    reduceMotion || vis.immediate
      ? 'none'
      : 'opacity 500ms cubic-bezier(0.16, 1, 0.3, 1)'

  return (
    <GlobalShortcuts>
      <div style={{ ...styles.container, opacity, transition }}>
        {/* Background Orbs */}
        <div className="bg-orb orb-1" />
        <div className="bg-orb orb-2" />
        <div className="bg-orb orb-3" />

        {/* Sidebar — marked for particle tracing via data-trace-shell on
            the Sidebar's own root <aside> so getBoundingClientRect() returns
            the real bounding box. Tracing only happens in specific-page
            mode; in Kodeon mode the attribute is harmless. */}
        <Sidebar />

        {/* Main Area */}
        <div style={styles.mainArea}>
          {/* Header — data-trace-shell is on the Header component's own
              <header> element. */}
          <Header />

          {/* Content + Right Panels */}
          <div style={styles.contentWrapper}>
            {/* Main Content */}
            <main data-trace-shell="main" style={styles.main}>
              <Outlet />
            </main>

            {/* Right Panel — Local Claude only.
                The Stream agents chat moved to the top-level /playground page,
                so the right rail no longer hosts the agent SessionPanel. */}
            {claudePanelOpen && ClaudeSessionPanel && (
              <div style={styles.rightPanelStack}>
                <div style={styles.panelTabStrip} role="tablist" aria-label="Right panels">
                  <button
                    style={{ ...styles.panelTab, ...styles.panelTabActive }}
                    role="tab"
                    aria-selected
                    data-testid="app-shell__tab--claude"
                    type="button"
                  >
                    Local Claude
                  </button>
                </div>
                <div style={styles.panelBody}>
                  <Suspense fallback={null}>
                    <ClaudeSessionPanel />
                  </Suspense>
                </div>
              </div>
            )}
          </div>
        </div>

        {/*
          Agent session popout — mounted once at the AppShell level so any
          authenticated surface (Queue inspector, AttentionInspector,
          automation-run StepCard, ...) can open it via
          `useQueueStore.openAgentSessionModal(sessionId)`. Mounted inside the
          AppShell (rather than at App.tsx) so the embedded react-router
          `<Link>` to "Open in Sessions Browser" has a router context.
        */}
        <AgentSessionModal />
        {/*
          Customer history popout — opened from any surface that knows a
          customer email / id (e.g. ApprovalHeaderActions in the queue).
          Mounted here alongside AgentSessionModal so the same router
          context is available inside the pop-out window.
        */}
        <CustomerHistoryModal />
        {/*
          Review thread popout — opened from any surface that knows a review
          interaction id (e.g. ApprovalHeaderActions in the queue). Mounted
          here alongside CustomerHistoryModal so the same router context is
          available inside the pop-out window, giving operators an identical
          pop-out workflow across support email history and review threads.
        */}
        <ReviewThreadModal />
        {/*
          Discuss-execution modal.  Operators trigger this from any
          AttentionItem with an `execution_id` (queue header action) or
          from the ExecutionDetailPage header.  Mounted alongside
          AgentSessionModal so the same router context is available for
          any deep-links rendered inside it.
        */}
        <DiscussExecutionModal />
      </div>
    </GlobalShortcuts>
  )
}

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    height: '100vh',
    // No background here. The <body> already paints bg-primary (see
    // global.css), and leaving the container transparent means the
    // StreamParticleCanvas (z-index 0, mounted at App root) is visible
    // through the transparent <main> area on /welcome — which is what
    // makes the "KODEON" particle text show in the empty content space.
    // The Sidebar and Header have their own opaque backgrounds, so they
    // continue to cover the canvas in their own regions.
    color: 'var(--text-primary)',
    position: 'relative',
    overflow: 'hidden',
  },
  mainArea: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
  },
  contentWrapper: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  main: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 20px',
  },
  rightPanelStack: {
    display: 'flex',
    flexDirection: 'column',
    width: '360px',
    flexShrink: 0,
    borderLeft: '1px solid var(--border-subtle)',
    overflow: 'hidden',
  },
  panelTabStrip: {
    display: 'flex',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
    flexShrink: 0,
  },
  panelTab: {
    flex: 1,
    padding: '8px 12px',
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    fontFamily: 'inherit',
    transition: 'color var(--transition-fast)',
  },
  panelTabActive: {
    color: 'var(--text-primary)',
    borderBottomColor: 'var(--accent-primary)',
  },
  panelBody: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}
